import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api';

export interface BrowsePage<T> { items: T[]; total: number; nextCursor: string | null }
export function reconcileBrowseRefresh<T extends { id: string }>(previous: BrowsePage<T>, next: BrowsePage<T>): { page: BrowsePage<T>; changed: boolean } {
    if (!previous.items.length) return { page: next, changed: false };
    if (previous.total !== next.total || previous.items.slice(0,60).map(item => item.id).join(',') !== next.items.map(item => item.id).join(',')) return { page: previous, changed: true };
    const updates = new Map(next.items.map(item => [item.id,item]));
    return { page: { ...previous, items: previous.items.map(item => updates.get(item.id) ?? item) }, changed: false };
}
export function browsePath(kind: string, parameters: Record<string, string | undefined>, cursor?: string | null) {
    const query = new URLSearchParams({ pageSize: '60' });
    for (const [key, value] of Object.entries(parameters)) if (value) query.set(key, value);
    if (cursor) query.set('cursor', cursor);
    return `/browse/${kind}?${query}`;
}
export function useBrowse<T extends { id: string }>(kind: string, parameters: Record<string, string | undefined>, refreshKey: number | string = 0) {
    const path = browsePath(kind, parameters);
    const [page, setPage] = useState<BrowsePage<T>>({ items: [], total: 0, nextCursor: null });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [attempt, setAttempt] = useState(0);
    const [changed,setChanged] = useState(false);
    const request = useRef(0);
    const previousConfiguration = useRef<{path:string;attempt:number} | null>(null);
    const pending = useRef(false);
    const pageRef = useRef(page); pageRef.current = page;
    useEffect(() => {
        const mutationRefresh = previousConfiguration.current?.path === path && previousConfiguration.current.attempt === attempt;
        previousConfiguration.current = {path,attempt};
        const generation = ++request.current;
        pending.current = true; setLoading(true); setError(null);
        setChanged(false);
        if (!mutationRefresh) setPage({ items: [], total: 0, nextCursor: null });
        const timer = setTimeout(() => {
            void api<BrowsePage<T>>(path).then(next => { if (request.current === generation) {
                if (mutationRefresh && pageRef.current.items.length > 60) {
                    const reconciled = reconcileBrowseRefresh(pageRef.current,next);
                    if (reconciled.changed) setChanged(true); else setPage(reconciled.page);
                } else setPage(next);
            } })
                .catch(err => { if (request.current === generation) setError(err instanceof Error ? err.message : 'List unavailable.'); })
                .finally(() => { if (request.current === generation) { pending.current = false; setLoading(false); } });
        }, 180);
        return () => { clearTimeout(timer); ++request.current; pending.current = false; };
    }, [path, refreshKey, attempt]);
    const checkUpdates = useCallback(() => {
        if (pending.current) return;
        const generation=request.current;
        pending.current=true;
        void api<BrowsePage<T>>(path).then(next=>{
            if(request.current!==generation)return;
            const reconciled=reconcileBrowseRefresh(pageRef.current,next);
            if(reconciled.changed){setChanged(true);return;}
            setPage(reconciled.page);
            setError(null);
        }).catch(err=>{if(request.current===generation)setError(err instanceof Error?err.message:"Refresh unavailable.");})
          .finally(()=>{if(request.current===generation)pending.current=false;});
    },[path]);
    const refresh=useCallback(()=>setAttempt(value=>value+1),[]);
    useEffect(() => {
        const timer = setInterval(checkUpdates, 8000);
        window.addEventListener('focus', checkUpdates);
        return () => { clearInterval(timer); window.removeEventListener('focus', checkUpdates); };
    }, [checkUpdates]);
    const loadMore = useCallback(() => {
        if (pending.current || !page.nextCursor) return;
        const generation = request.current;
        pending.current = true; setLoading(true); setError(null);
        void api<BrowsePage<T>>(`${path}&cursor=${encodeURIComponent(page.nextCursor)}`).then(next => {
            if (request.current === generation) setPage(current => ({ ...next, items: [...new Map([...current.items, ...next.items].map(item => [item.id, item])).values()] }));
        }).catch(err => { if (request.current === generation) setError(err instanceof Error ? err.message : 'List unavailable.'); })
            .finally(() => { if (request.current === generation) { pending.current = false; setLoading(false); } });
    }, [path, page.nextCursor]);
    return { ...page, loading, error, loadMore, refresh, changed, version: attempt, retry: () => page.items.length && page.nextCursor ? loadMore() : setAttempt(value => value + 1) };
}
